import { CURRENCY_CONFIG as SHARED_CURRENCY_CONFIG, formatCurrency as sharedFormatCurrency } from '../../utils/currency';

/**
 * Recorded date mirrors the invoice (sent) date until it's set independently.
 * Given the CURRENT values before an invoice-date edit, returns true while the
 * two are still in sync (or recorded is blank) — i.e. the invoice-date change
 * should carry across to the recorded date. Shared by the Add/Detail modals and
 * the invoice editor so the mirror behaviour stays consistent.
 */
export function shouldMirrorRecordedDate(prevRecordedDate: string, prevInvoiceDate: string): boolean {
  return !prevRecordedDate || prevRecordedDate === prevInvoiceDate;
}

// =======================================
// DATA TYPES
// =======================================

/**
 * Invoice data structure as received from the database
 */
export interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  /** Date the invoice is attributed to for budgeting/period reporting. Defaults
   * to invoice_date but can be set independently. */
  recorded_date: string;
  project_id: number | null;
  stage_id: number | null;
  job_number: string | null;
  project_description: string | null;
  client_name: string | null;
  our_fee: number;
  disbursements: number;
  gst_amount: number;
  total_amount: number;
  status: string;
  notes: string | null;
  claim_title: string | null;
  service_description: string | null;
  line_item_count: number;
  project_type: string | null;
  bundled_in_invoice_id: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Project data structure for job selection dropdown
 */
export interface ProjectOption {
  id: number;
  job_number: number;
  display_name: string;
  status: string;
  entity_type: string;
  parent_project_id: number | null;
  stage_id: string | null;
  stage_description: string | null;
  parent_description: string | null;
  effective_project_lead: string | null;
}

// =======================================
// INVOICE LINE ITEM TYPES
// =======================================

export type LineItemType = 'item' | 'disbursement' | 'text' | 'bullet' | 'deduction' | 'header' | 'spacer' | 'phase' | 'phase_label' | 'prior_claims_deduction' | 'drawing';

/**
 * Line types that carry no quantity, rate or amount: they only put words on
 * the page. One list, so a new unpriced type cannot be priced by one code path
 * and blank in another.
 */
export const isUnpricedLineType = (type: LineItemType): boolean =>
  type === 'text' || type === 'bullet' || type === 'header' || type === 'spacer' || type === 'drawing';

export interface InvoiceLineItem {
  id?: number;
  sort_order: number;
  line_type: LineItemType;
  description: string;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  team_member_id: string | null;
  team_member_name?: string | null;
  team_member_type?: string | null;
  service_rate_id: number | null;
  indent_level: number;
  /**
   * Blank-line spacing around the line. Null means "the default for this
   * line_type" (see LINE_SPACING_DEFAULTS), so existing rows need no backfill.
   */
  space_above?: boolean | null;
  space_below?: boolean | null;
  phase?: FeePhase | null;
  discipline?: Discipline | null;
  /**
   * A 'drawing' line's number and issue; its title is `description`. Copied
   * from the stage's drawing set by the RWD templates (or typed), never joined
   * to it, so revising the set cannot change an issued invoice.
   */
  drawing_number?: string | null;
  issue_number?: string | null;
}

/**
 * A drawing line as one string -- "5910-13A-300  Issue C  Retaining Wall
 * Plan" -- the form the RWD templates wrote before the 'drawing' type existed.
 * Used when a drawing line is changed to another type, so its number and issue
 * are kept in the text rather than silently dropped.
 */
export const drawingLineText = (item: Pick<InvoiceLineItem, 'drawing_number' | 'issue_number' | 'description'>): string =>
  [
    item.drawing_number?.trim(),
    item.issue_number?.trim() ? `Issue ${item.issue_number.trim()}` : null,
    item.description.trim(),
  ].filter(Boolean).join('  ');

/**
 * The reverse of drawingLineText: split "5910-13A-300  Issue C  Title" into its
 * parts, or null when the text is not in that form. Changing such a bullet
 * (an old template line, or a drawing line changed and changed back) to a
 * drawing line then fills all three cells instead of putting the whole string
 * in the title. The same pattern as the convert_rwd_drawing_bullets migration.
 */
export const parseDrawingLineText = (
  text: string,
): { drawing_number: string; issue_number: string; description: string } | null => {
  const m = /^(.+?) {2}Issue (.*?) {2}(.*)$/.exec(text.trim());
  if (!m) return null;
  return { drawing_number: m[1].trim(), issue_number: m[2].trim(), description: m[3].trim() };
};

/**
 * Blank-line spacing around a line item. Each side asks for one blank line, and
 * adjacent lines SHARE the gap between them: it is one blank line, not two.
 *
 * Either side is enough to open it, so "space above this line" does what it
 * says with a single click rather than needing the line before it to agree.
 * Closing it is handled in the editor, which clears both sides of that one gap
 * at once - see handleToolbarToggleSpacing.
 */
export interface LineSpacing {
  above: boolean;
  below: boolean;
}

/**
 * Per-type defaults, chosen so an invoice reads like a document out of the box:
 * priced rows breathe, a run of bullets or text lines is tight, and a heading
 * sits hard against the list it introduces while still getting a blank line
 * above it from the priced row before it.
 *
 * The rhythm is expressed on `below` only. With either-side-opens semantics, a
 * type that also defaulted `above` on would force a gap after every heading,
 * which is exactly where a document wants none.
 */
export const LINE_SPACING_DEFAULTS: Record<LineItemType, LineSpacing> = {
  item: { above: false, below: true },
  disbursement: { above: false, below: true },
  deduction: { above: false, below: true },
  phase: { above: false, below: true },
  prior_claims_deduction: { above: false, below: true },
  header: { above: false, below: false },
  phase_label: { above: false, below: false },
  text: { above: false, below: false },
  bullet: { above: false, below: false },
  drawing: { above: false, below: false },
  spacer: { above: false, below: false },
};

export const getLineSpacing = (
  item: Pick<InvoiceLineItem, 'line_type' | 'space_above' | 'space_below'>,
): LineSpacing => {
  const fallback = LINE_SPACING_DEFAULTS[item.line_type] ?? { above: true, below: true };
  return {
    above: item.space_above ?? fallback.above,
    below: item.space_below ?? fallback.below,
  };
};

/**
 * Whether a blank line sits between two consecutive lines. Either side asking
 * for it is enough. The editor, its pagination and InvoicePDF all read this one
 * function, so the rule cannot drift between the preview and the export.
 */
export const hasGapBetween = (
  above: Pick<InvoiceLineItem, 'line_type' | 'space_above' | 'space_below'> | undefined,
  below: Pick<InvoiceLineItem, 'line_type' | 'space_above' | 'space_below'>,
): boolean => !!above && (getLineSpacing(above).below || getLineSpacing(below).above);

export interface ServiceRate {
  id: number;
  job_type: string;
  rate_code: string;
  description: string;
  rate: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InvoiceDetailProject {
  id: number;
  job_number: number;
  description: string;
  types: string;
  po: string | null;
  primary_contact: number | null;
  startup_meeting: boolean;
  number_of_inspections: number | null;
  requires_qa: boolean;
  street_address: string | null;
  city: string | null;
  care_of: string | null;
}

// Design and Advisory invoices use the progress-claim flow (phased fees +
// cumulative %), drive the address line, and share other UI affordances
// that RWC/RWD invoices don't get.
export const isProgressClaimProjectType = (types?: string | null): boolean =>
  types === 'Design' || types === 'Advisory';

// Site address on invoices renders street + suburb only — state/postcode/
// country are deliberately stripped.
export const formatSiteAddress = (
  project?: { street_address?: string | null; city?: string | null } | null,
): string =>
  [project?.street_address, project?.city].filter(Boolean).join(', ');

export interface InvoiceDetailStage {
  id: number;
  stage_id: string;
  stage_description: string;
  startup_meeting: boolean;
  number_of_inspections: number | null;
  requires_qa: boolean;
  po: string | null;
  care_of: string | null;
}

export type ClientAddressType = 'street' | 'po_box' | 'email';

export interface InvoiceDetailClient {
  id: number;
  company_name: string;
  address_type: string | null;
  street_address: string | null;
  po_box: string | null;
  postal_address: string | null;
  header_lines: string | null;
  billing_email: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
}

// Resolve the "c/-" care-of for an invoice: an explicit invoice override wins,
// then the stage value, then the project value. Empty/blank strings are treated
// as absent so a cleared override falls through to project/stage.
export const resolveCareOf = (
  invoice?: { care_of_override?: string | null } | null,
  stage?: { care_of?: string | null } | null,
  project?: { care_of?: string | null } | null,
): string | null => {
  const pick = (v?: string | null) => (v && v.trim() ? v.trim() : null);
  return pick(invoice?.care_of_override) ?? pick(stage?.care_of) ?? pick(project?.care_of);
};

// Split a multiline field (header lines / postal address) into rendered lines,
// dropping blank lines. Values are rendered verbatim (no case transform).
const splitLines = (value?: string | null): string[] =>
  (value ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

const STATE_ABBREVIATIONS: Record<string, string> = {
  'western australia': 'WA', 'new south wales': 'NSW', 'victoria': 'VIC',
  'queensland': 'QLD', 'south australia': 'SA', 'tasmania': 'TAS',
  'northern territory': 'NT', 'australian capital territory': 'ACT',
};

export const abbreviateState = (state?: string | null): string | null => {
  if (!state) return null;
  return STATE_ABBREVIATIONS[state.toLowerCase()] || state;
};

export interface ClientAddressLines {
  headerLines: string[];   // rendered verbatim above the top name line
  // The bold top line: the care-of party when a care-of is set, otherwise the
  // client's own company name. Rendered VERBATIM by every surface -- see the
  // note on toTitleCase below.
  topName: string;
  // When a care-of is set, the client's company name to render below the top
  // line as "c/- {name}" (caller adds the "c/- " prefix). Verbatim, as above.
  // Null when there is no care-of.
  careOfClientName: string | null;
  // Body lines, already in their final display case so renderers show them
  // verbatim: street is Title Cased; PO box / email keep exactly what was
  // entered (a postal line like "Locked Bag 44" or an email keeps its case).
  addressLines: string[];
  cityLine: string | null; // "CITY STATE POSTCODE" (bold); null for email
}

// Title Case: lower-case then capitalise the first letter of each word, so the
// result is consistent regardless of how the source was stored (UPPER/lower).
//
// Applied to the STREET LINE ONLY, and deliberately not to the company name.
// It lower-cases first, so it destroys any capitalisation the source was
// relying on: a client entered as "MME Underground Services" printed as "Mme
// Underground Services". Company names are the field where an acronym is
// normal and the entered case is the intended case, so both invoices and
// documents now render topName / careOfClientName exactly as stored, and the
// client record is the single place to fix a name that reads wrong.
const toTitleCase = (str: string): string =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Build the ordered set of client-address lines for an invoice. Keeps the PDF
// declarative: the renderer just maps these arrays to styled <Text> nodes.
//
// Care-of layout (the owner pays, but our client manages/receives the invoice):
//   {care-of party}        ← top/bold line: the owner the invoice is addressed to
//   c/- {client name}      ← our client, who manages it
//   {address}
// i.e. the invoice is addressed to the paying owner, c/- the managing client.
export const buildClientAddressLines = (
  client: Pick<
    InvoiceDetailClient,
    | 'company_name' | 'address_type' | 'street_address' | 'postal_address'
    | 'header_lines' | 'billing_email' | 'city' | 'state' | 'postcode'
  >,
  careOf?: string | null,
): ClientAddressLines => {
  const cityLine =
    [client.city, abbreviateState(client.state), client.postcode]
      .filter(Boolean)
      .join(' ') || null;

  let addressLines: string[];
  let showCityLine = true;
  switch (client.address_type) {
    case 'email':
      addressLines = client.billing_email ? [`Via email: ${client.billing_email}`] : [];
      showCityLine = false; // email format has no postal locality line
      break;
    case 'po_box':
      addressLines = splitLines(client.postal_address); // verbatim, as entered
      break;
    case 'street':
    default:
      addressLines = client.street_address ? [toTitleCase(client.street_address)] : [];
      break;
  }

  const trimmedCareOf = careOf && careOf.trim() ? careOf.trim() : null;

  return {
    headerLines: splitLines(client.header_lines),
    topName: trimmedCareOf ?? client.company_name,
    careOfClientName: trimmedCareOf ? client.company_name : null,
    addressLines,
    cityLine: showCityLine ? cityLine : null,
  };
};

export interface InvoiceDetailContact {
  id: number;
  title: string | null;
  first_name: string;
  last_name: string;
}

export type FeeStatus = 'Draft' | 'Sent' | 'Accepted' | 'Rejected' | 'Superseded';
export type FeeComponentType = 'time' | 'allowance' | 'expense' | 'item';
export type FeeRowStatus = FeeStatus | 'no_fee';
export type FeePhase =
  // Mirrors the DB fee_phase enum, in its order. 'Probable' is a whole-job
  // lump sum for work not yet awarded; it is always is_probable (DB CHECK)
  // and is deleted once the job is won and the real phases are priced.
  | 'Probable'
  | 'Preliminary'
  | 'Design & Documentation'
  | 'Schematic Design'
  | 'Design Development'
  | 'Contract Documentation'
  | 'Construction Administration'
  | 'Defect Liability'
  | 'Site Visit'
  | 'Custom';

// Standard phases in canonical lifecycle order. 'Custom' is intentionally
// excluded — multiple custom rows are allowed per fee plan and they are
// added via a separate "+ Custom" affordance, not picked from this list.
export const FEE_PHASES: FeePhase[] = [
  // First deliberately. This is the phase you reach for before any real phase
  // exists — one lump sum for a job you may not be engaged for — so it leads
  // the add-phase bar, and because the section sort keys off indexOf() here,
  // it also sorts to the top of the fee.
  'Probable',
  'Preliminary',
  'Design & Documentation',
  'Schematic Design',
  'Design Development',
  'Contract Documentation',
  'Construction Administration',
  'Defect Liability',
  'Site Visit',
];

// Engineering discipline. Mirrors the DB `drawing_discipline` enum. A null
// discipline means single-discipline (legacy / civil-scope off) and is
// treated as 'structural' everywhere it matters.
export type Discipline = 'civil' | 'structural';

// Structural first, then civil — the canonical display/sort order used by the
// fee editor, invoice grouping, and the progress tab.
export const DISCIPLINES: Discipline[] = ['structural', 'civil'];

export const disciplineLabel = (discipline: Discipline | null | undefined): string =>
  discipline === 'civil' ? 'Civil' : 'Structural';

// Canonical "null == structural" normalisation. This MUST match the DB's
// COALESCE(discipline, 'structural') used in the unique indexes / ON CONFLICT
// targets — keep it the single source of truth so the frontend dedup/match
// logic can never drift from the database buckets.
export const normDiscipline = (discipline: Discipline | null | undefined): Discipline =>
  discipline === 'civil' ? 'civil' : 'structural';

export const sameDiscipline = (
  a: Discipline | null | undefined,
  b: Discipline | null | undefined,
): boolean => normDiscipline(a) === normDiscipline(b);

export interface ProjectFeeComponent {
  id?: number;
  section_id?: number;
  sort_order: number;
  component_type: FeeComponentType;
  description: string;
  team_member_type: string | null;
  hours: number | null;
  rate: number | null;
  amount: number;
  notes?: string | null;
}

export interface ProjectFeeSection {
  id?: number;
  fee_id?: number;
  phase: FeePhase;
  discipline?: Discipline | null;
  sort_order: number;
  name: string | null;
  notes?: string | null;
  fee_total: number;
  // When true this section is claimed by item COUNT (one 'item' component,
  // billed per-period) rather than by % complete. Defaults to false; the
  // FeesTab defaults it to true for the 'Site Visit' phase.
  is_item_based?: boolean;
  // When true this phase is quoted but not yet awarded — Construction
  // Administration is the usual case. The Budget page reports these amounts
  // in its own Probable column and keeps them out of Unallocated. Survives
  // the job being won; cleared by hand, or automatically the first time the
  // phase is billed for value.
  is_probable?: boolean;
  components: ProjectFeeComponent[];
  // How many progress_claims rows currently reference this section. > 0
  // means the section has been (partially) billed — the FeesTab locks
  // edits for it and routes new work through the variation flow instead.
  progress_claim_count?: number;
  /**
   * Value claimed against this section so far. Distinct from
   * `progress_claim_count`, which also counts 0% rows — a phase shown on an
   * invoice without being billed. Mirrors set_fee_section_probable's guard:
   * > 0 means the phase was awarded, so it can no longer be marked probable.
   */
  claimed_to_date?: number;
  /**
   * What has been invoiced against this section, for DISPLAY — the most
   * recent cumulative claim (Draft invoices included), never a sum of them.
   * Read-only, returned by get_project_fee_for_entity; it is NOT part of any
   * section save payload. Do not show `claimed_to_date` instead: that one is
   * a sum of cumulative claims and only meaningful as a `> 0` test.
   */
  invoiced_to_date?: number;
  /** Item-based sections only: count invoiced so far, and the quoted count. */
  invoiced_qty?: number | null;
  qty_total?: number | null;
  // Client-only stable key for new (unsaved) sections, used by the FeesTab
  // amend-mode save to map the inserted_section_ids RPC response back to
  // the right row even if the user reordered/removed sections during the
  // in-flight save. Never sent to the server; cleared once the section
  // has a real `id`.
  _localKey?: string;
}

export interface ProjectFee {
  id: number;
  project_id: number;
  fee_number: string | null;
  fee_date: string;
  status: FeeStatus;
  accepted_at: string | null;
  accepted_by: string | null;
  revision_of: number | null;
  total_ex_gst: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  sections: ProjectFeeSection[];
}

export interface ProgressClaim {
  id?: number;
  invoice_id?: number;
  phase: FeePhase;
  discipline?: Discipline | null;
  fee_id: number | null;
  section_id: number | null;
  quoted_fee: number;
  percent_complete: number;
  amount_to_date: number;
  amount_this_period: number;
  sort_order: number;
}

export interface ProgressClaimPhaseContext {
  phase: FeePhase;
  discipline?: Discipline | null;
  section_id: number;
  /** User-supplied label on the section (used as the heading for `phase: 'Custom'` rows). */
  section_name: string | null;
  sort_order: number;
  quoted_fee: number;
  prior_percent_complete: number;
  prior_amount_to_date: number;
  /**
   * Quoted scope we have not been awarded. The row still renders — the client
   * should see the scope exists — but it cannot be claimed for value until
   * Probable is unticked on the fee. enforce_probable_not_claimed refuses it
   * server-side regardless of what the UI allows.
   */
  is_probable?: boolean;
}

/**
 * Derives the human-readable phase label for a fee section. For Custom phases
 * we use the user-typed `section_name`; for standard phases we use the enum
 * value verbatim. Falls back to "Custom phase" if a Custom row is unnamed.
 */
export const phaseDisplayLabel = (phase: FeePhase, sectionName: string | null | undefined): string => {
  if (phase === 'Custom') {
    return (sectionName && sectionName.trim()) || 'Custom phase';
  }
  return phase;
};

/**
 * An item-based fee section's single 'item' component, claimed by count.
 * Cumulative-to-date, like percentage phases. `qty_total` is the quoted count
 * (e.g. 6 site visits); `prior_qty_claimed` is the to-date count on the most
 * recent prior Sent/Entered invoice carrying this item (matched by phase +
 * discipline + description) — NOT a sum across invoices. Returned by
 * get_progress_claim_context.
 */
export interface ProgressClaimItemContext {
  section_id: number;
  phase: FeePhase;
  discipline?: Discipline | null;
  section_name: string | null;
  sort_order: number;
  description: string;
  rate: number;
  qty_total: number;
  prior_qty_claimed: number;
  /**
   * Quoted scope we have not been awarded. The row still renders — the client
   * should see the scope exists — but it cannot be claimed for value until
   * Probable is unticked on the fee. enforce_probable_not_claimed refuses it
   * server-side regardless of what the UI allows.
   */
  is_probable?: boolean;
}

/**
 * UI-facing item claim row: context plus this invoice's cumulative to-date
 * `count` (canonical on the item line's quantity). `remaining` = qty_total −
 * count; the count claimed this period is `count − prior_qty_claimed`.
 */
export interface ProgressItemClaim extends ProgressClaimItemContext {
  remaining: number;
  count: number;
}

export interface ProgressClaimContext {
  accepted_fee_id: number | null;
  phases: ProgressClaimPhaseContext[];
  items: ProgressClaimItemContext[];
  prior_invoice_id: number | null;
  /**
   * Cumulative net `our_fee` across prior Sent/Entered, non-bundled invoices
   * on the same project + stage. Used as the canonical "Less previous claims"
   * amount — captures phases, count-based items, and ad-hoc lines alike (all
   * are cumulative-to-date and telescope through each invoice's own LPC), and
   * works for modal-only historical invoices.
   */
  prior_invoiced_total?: number;
  prior_line_items: InvoiceLineItem[];
}

export interface InvoiceDetailData {
  invoice: {
    id: number;
    invoice_number: string;
    invoice_date: string;
    recorded_date: string;
    project_id: number;
    stage_id: number | null;
    our_fee: number;
    disbursements: number;
    gst_amount: number;
    total_amount: number;
    status: string;
    notes: string | null;
    claim_title: string | null;
    claim_description: string | null;
    service_description: string | null;
    po_number: string | null;
    contact_override: string | null;
    project_description_override: string | null;
    site_address_override: string | null;
    care_of_override: string | null;
    bundled_in_invoice_id: number | null;
    created_at: string;
    updated_at: string;
  };
  line_items: InvoiceLineItem[];
  project: InvoiceDetailProject | null;
  stage: InvoiceDetailStage | null;
  client: InvoiceDetailClient | null;
  contact: InvoiceDetailContact | null;
  progress_claims?: ProgressClaim[];
  prior_service_description?: string | null;
  invoice_position?: number;
  bundled_invoices?: BundledInvoiceRef[];
  bundling_target?: BundlingTarget | null;
}

export interface BundlingTarget {
  id: number;
  invoice_number: string;
  invoice_date: string;
  status: string;
}

export interface BundledInvoiceRef {
  id: number;
  invoice_number: string;
  invoice_date: string;
  our_fee: number;
  line_items: InvoiceLineItem[];
}

export interface SidebarAllocation {
  id: number;
  date: string;
  employee_id: string;
  employee_name: string;
  employee_type: string | null;
  description: string | null;
  hours: number;
  charge_rate: number;
  total_value: number;
}

export interface SidebarPriorInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  status: string;
  total_amount: number;
  our_fee?: number;
  bundled_in_invoice_id?: number | null;
}

export interface InvoiceSidebarData {
  allocations: SidebarAllocation[];
  prior_invoices: SidebarPriorInvoice[];
}

export const FINAL_CLAIM_TITLE = 'Final Claim';

/**
 * Resolve the displayed/persisted claim title from saved value + ordinal position.
 * - "Final Claim" (final marker) is preserved as-is.
 * - Empty value defaults to "Progress Claim N" using the current position.
 * - Any saved value (including "Progress Claim N") is preserved verbatim. This
 *   lets the Fields-tab "Progress Claim Number" override stick during the
 *   migration period where projects start at a non-zero claim number, and
 *   any manually-typed claim title survives a re-render without being
 *   auto-overwritten.
 */
export function resolveClaimTitle(saved: string | null | undefined, position: number | undefined): string {
  const value = saved?.trim() ?? '';
  const ordinal = position && position > 0 ? position : 1;
  if (value === FINAL_CLAIM_TITLE) return FINAL_CLAIM_TITLE;
  if (value === '') return `Progress Claim ${ordinal}`;
  return value;
}

/**
 * Compute totals from line items
 */
export function computeLineItemTotals(lineItems: InvoiceLineItem[]): {
  ourFee: number;
  disbursements: number;
  subtotal: number;
  gst: number;
  total: number;
} {
  let ourFee = 0;
  let disbursements = 0;

  for (const item of lineItems) {
    if (item.line_type === 'item' || item.line_type === 'phase') {
      ourFee += item.amount ?? 0;
    } else if (item.line_type === 'disbursement') {
      disbursements += item.amount ?? 0;
    } else if (item.line_type === 'deduction' || item.line_type === 'prior_claims_deduction') {
      ourFee -= Math.abs(item.amount ?? 0);
    }
  }

  const subtotal = ourFee + disbursements;
  const gst = subtotal * 0.10;
  const total = subtotal + gst;

  return { ourFee, disbursements, subtotal, gst, total };
}

// =======================================
// COMPONENT CONFIGURATION
// =======================================

/**
 * Column configuration interface for table display
 */
export interface ColumnConfig {
  header: string;
  accessor: string;
  sortable?: boolean;
  width?: string;
  component?: string;
}

/**
 * Modal field configuration for form rendering
 */
export interface ModalFieldConfig {
  type: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'readonly' | 'currency';
  label: string;
  required?: boolean;
  section: 'basic' | 'financial' | 'other';
  order: number;
  options?: Array<{ id: string; label: string; value: string }>;
  placeholder?: string;
  disabled?: boolean;
  step?: string;
  min?: string;
  max?: string;
}

/**
 * Invoice filters interface for server-side filtering
 */
export interface InvoiceFilters {
  search_term?: string | null;
  status_filter?: string[] | null;
  client_filter?: string | null;
  from_date?: string | null;
  to_date?: string | null;
  types_filter?: string[] | null;
  project_status_filter?: string[] | null;
  from_invoice_number?: number | null;
  to_invoice_number?: number | null;
  page_size?: number;
  page_offset?: number;
  sort_by?: string;
  sort_order?: string;
}

/**
 * Summary totals for filtered invoices
 */
export interface InvoiceSummary {
  total_our_fee: number;
  total_disbursements: number;
  total_invoiced: number;
  total_count: number;
  draft_count: number;
  sent_count: number;
  entered_count: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Default pagination settings
 */
export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Invoice status options for filtering and forms
 */
export const INVOICE_STATUS_OPTIONS = [
  { id: 'Draft', label: 'Draft', value: 'Draft' },
  { id: 'Sent', label: 'Sent', value: 'Sent' },
  { id: 'Entered', label: 'Entered', value: 'Entered' }
] as { id: string; label: string; value: string; }[];

/**
 * Project job type options (from projects.types enum)
 */
export const JOB_TYPE_OPTIONS = [
  'Design',
  'Advisory',
  'RWC',
  'RWD'
] as const;

export type JobType = typeof JOB_TYPE_OPTIONS[number];

/**
 * Sorting options for the invoice table
 */
export const SORT_OPTIONS = [
  { id: 'invoice_date', label: 'Sent', value: 'invoice_date' },
  { id: 'recorded_date', label: 'Recorded', value: 'recorded_date' },
  { id: 'invoice_number', label: 'Invoice Number', value: 'invoice_number' },
  { id: 'job_number', label: 'Job Number', value: 'job_number' },
  { id: 'total_amount', label: 'Total Amount', value: 'total_amount' },
  { id: 'status', label: 'Status', value: 'status' }
] as const;

/**
 * Currency formatting configuration (re-exported from shared util)
 */
export const CURRENCY_CONFIG = SHARED_CURRENCY_CONFIG;

// =======================================
// COMPONENT SETTINGS
// =======================================

/**
 * Table column configuration for invoice display
 * Ordered: Inv #, Date, Job #, Client, Description, Our Fee, Disburse, Total, Status
 * Note: Column widths controlled by CSS nth-child selectors (like ProjectRegister)
 */
export const invoiceColumns: Record<string, ColumnConfig> = {
  invoice_number: {
    header: 'Inv #',
    accessor: 'invoice_number',
    sortable: true
  },
  job_number: {
    header: 'Job #',
    accessor: 'job_number',
    sortable: true
  },
  invoice_date: {
    header: 'Sent',
    accessor: 'invoice_date',
    sortable: true,
    component: 'DateColumn'
  },
  recorded_date: {
    header: 'Recorded',
    accessor: 'recorded_date',
    sortable: true,
    component: 'DateColumn'
  },
  client_name: {
    header: 'Client',
    accessor: 'client_name',
    sortable: false
  },
  project_description: {
    header: 'Description',
    accessor: 'project_description',
    sortable: false
  },
  our_fee: {
    header: 'Our Fee',
    accessor: 'our_fee',
    sortable: true,
    component: 'CurrencyColumn'
  },
  disbursements: {
    header: 'Disburse',
    accessor: 'disbursements',
    sortable: false,
    component: 'CurrencyColumn'
  },
  total_amount: {
    header: 'Total',
    accessor: 'total_amount',
    sortable: true,
    component: 'CurrencyColumn'
  },
  status: {
    header: 'Status',
    accessor: 'status',
    sortable: true,
    component: 'StatusColumn'
  }
};

/**
 * Modal form field configuration for invoice details
 */
export const invoiceModalFields: Record<string, ModalFieldConfig> = {
  // Basic Information Section
  invoice_number: {
    type: 'readonly',
    label: 'Invoice Number',
    section: 'basic',
    order: 1
  },
  invoice_date: {
    type: 'date',
    label: 'Invoice Date',
    required: true,
    section: 'basic',
    order: 2
  },
  job_number: {
    type: 'select',
    label: 'Job Number',
    required: true,
    section: 'basic',
    order: 3,
    placeholder: 'Search and select job...'
  },
  status: {
    type: 'select',
    label: 'Status',
    options: INVOICE_STATUS_OPTIONS,
    required: true,
    section: 'basic',
    order: 4
  },
  
  // Financial Section
  our_fee: {
    type: 'currency',
    label: 'Our Fee',
    required: true,
    section: 'financial',
    order: 1,
    step: '0.01',
    min: '0'
  },
  disbursements: {
    type: 'currency',
    label: 'Disbursements',
    section: 'financial',
    order: 2,
    step: '0.01',
    min: '0'
  },
  gst_amount: {
    type: 'readonly',
    label: 'GST (10%)',
    section: 'financial',
    order: 3
  },
  total_amount: {
    type: 'readonly',
    label: 'Total Amount',
    section: 'financial',
    order: 4
  },
  
  // Other Section
  notes: {
    type: 'textarea',
    label: 'Notes',
    section: 'other',
    order: 1,
    placeholder: 'Optional notes about this invoice...'
  },
  created_at: {
    type: 'readonly',
    label: 'Created',
    section: 'other',
    order: 2
  },
  updated_at: {
    type: 'readonly',
    label: 'Updated',
    section: 'other',
    order: 3
  }
};

/**
 * Helper function to format currency values (re-exported from shared util)
 */
export const formatCurrency = sharedFormatCurrency;

/**
 * Invoice date in the long form the letterhead uses, e.g. "1st August 2026".
 * Shared by the editor preview and the PDF so they can never disagree.
 */
export const formatInvoiceDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDate();
  const month = date.toLocaleDateString('en-AU', { month: 'long' });
  return `${day}${getOrdinalSuffix(day)} ${month} ${date.getFullYear()}`;
};

const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Helper function to format dates
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Helper function to calculate GST amount
 */
export const calculateGST = (ourFee: number, disbursements: number): number => {
  return (ourFee + disbursements) * 0.10;
};

/**
 * Helper function to calculate total amount including GST
 */
export const calculateTotal = (ourFee: number, disbursements: number): number => {
  const subtotal = ourFee + disbursements;
  const gst = subtotal * 0.10;
  return subtotal + gst;
};

// =======================================
// PDF EXPORT TYPES AND HELPERS
// =======================================

/**
 * Human-readable filter summary for PDF export header
 */
export interface FilterSummary {
  status: string;
  client: string;
  dateRange: string;
  jobTypes: string;
  projectStatus: string;
  invoiceRange: string;
  search: string;
}

/**
 * Builds a human-readable filter summary from active filter state
 */
export function buildFilterSummary(options: {
  filterStatus: string;
  clientName: string | null;
  dateRangePreset: string;
  filterFromDate: string;
  filterToDate: string;
  selectedTypes: string[];
  selectedProjectStatus: string[];
  filterFromInvoice: string;
  filterToInvoice: string;
  searchInput: string;
}): FilterSummary {
  const {
    filterStatus, clientName, dateRangePreset,
    filterFromDate, filterToDate, selectedTypes,
    selectedProjectStatus, filterFromInvoice, filterToInvoice, searchInput
  } = options;

  let dateRange = 'All Time';
  if (dateRangePreset === 'custom' && (filterFromDate || filterToDate)) {
    const from = filterFromDate ? formatDate(filterFromDate) : '...';
    const to = filterToDate ? formatDate(filterToDate) : '...';
    dateRange = `${from} – ${to}`;
  } else {
    const preset = DATE_RANGE_PRESETS.find(p => p.value === dateRangePreset);
    if (preset) dateRange = preset.label;
  }

  let invoiceRange = '';
  if (filterFromInvoice && filterToInvoice) {
    invoiceRange = `${filterFromInvoice} – ${filterToInvoice}`;
  } else if (filterFromInvoice) {
    invoiceRange = `From ${filterFromInvoice}`;
  } else if (filterToInvoice) {
    invoiceRange = `Up to ${filterToInvoice}`;
  }

  return {
    status: filterStatus === 'All' ? 'All' : filterStatus,
    client: clientName || 'All',
    dateRange,
    jobTypes: selectedTypes.length > 0 ? selectedTypes.join(', ') : 'All',
    projectStatus: selectedProjectStatus.length > 0 ? selectedProjectStatus.join(', ') : 'All',
    invoiceRange,
    search: searchInput || '',
  };
}

// =======================================
// DATE RANGE PRESETS AND UTILITIES
// =======================================

/**
 * Date range preset options for invoices
 */
export const DATE_RANGE_PRESETS = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Quarter', value: 'thisQuarter' },
  { label: 'This Financial Year', value: 'thisFinancialYear' },
  { label: 'Last Financial Year', value: 'lastFinancialYear' },
  { label: 'All Time', value: 'allTime' },
  { label: 'Custom Range', value: 'custom' }
] as const;

/**
 * Helper function to format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate date ranges for invoice presets
 * Australian Financial Year: July 1 - June 30
 */
export function getInvoiceDateRange(preset: string): { from: string; to: string } {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-based (0 = January)
  
  switch (preset) {
    case 'allTime': {
      return {
        from: '',
        to: ''
      };
    }
    
    case 'thisMonth': {
      const startOfMonth = new Date(currentYear, currentMonth, 1);
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      return {
        from: formatDateLocal(startOfMonth),
        to: formatDateLocal(endOfMonth)
      };
    }
    
    case 'lastMonth': {
      const startOfLastMonth = new Date(currentYear, currentMonth - 1, 1);
      const endOfLastMonth = new Date(currentYear, currentMonth, 0);
      return {
        from: formatDateLocal(startOfLastMonth),
        to: formatDateLocal(endOfLastMonth)
      };
    }
    
    case 'thisQuarter': {
      const quarterStart = Math.floor(currentMonth / 3) * 3;
      const startOfQuarter = new Date(currentYear, quarterStart, 1);
      const endOfQuarter = new Date(currentYear, quarterStart + 3, 0);
      return {
        from: formatDateLocal(startOfQuarter),
        to: formatDateLocal(endOfQuarter)
      };
    }
    
    case 'thisFinancialYear': {
      // Australian FY: July 1 - June 30
      const fyStartYear = currentMonth >= 6 ? currentYear : currentYear - 1; // July is month 6
      const startOfFY = new Date(fyStartYear, 6, 1); // July 1
      const endOfFY = new Date(fyStartYear + 1, 5, 30); // June 30
      return {
        from: formatDateLocal(startOfFY),
        to: formatDateLocal(endOfFY)
      };
    }
    
    case 'lastFinancialYear': {
      // Previous Australian FY
      const fyStartYear = currentMonth >= 6 ? currentYear - 1 : currentYear - 2;
      const startOfLastFY = new Date(fyStartYear, 6, 1); // July 1
      const endOfLastFY = new Date(fyStartYear + 1, 5, 30); // June 30
      return {
        from: formatDateLocal(startOfLastFY),
        to: formatDateLocal(endOfLastFY)
      };
    }
    
    default: {
      // Custom or default
      const to = formatDateLocal(today);
      return { from: to, to };
    }
  }
}
